'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { resolveApiBase } from './api';

export const LANGUAGES = {
  cs: { label: 'Čeština', flag: '🇨🇿' },
  sk: { label: 'Slovenčina', flag: '🇸🇰' },
  en: { label: 'English', flag: '🇬🇧' },
  de: { label: 'Deutsch', flag: '🇩🇪' },
  fr: { label: 'Français', flag: '🇫🇷' },
  es: { label: 'Español', flag: '🇪🇸' },
  it: { label: 'Italiano', flag: '🇮🇹' },
  pt: { label: 'Português', flag: '🇵🇹' },
  nl: { label: 'Nederlands', flag: '🇳🇱' },
  pl: { label: 'Polski', flag: '🇵🇱' },
  hu: { label: 'Magyar', flag: '🇭🇺' },
  sv: { label: 'Svenska', flag: '🇸🇪' },
  no: { label: 'Norsk', flag: '🇳🇴' },
  da: { label: 'Dansk', flag: '🇩🇰' },
  fi: { label: 'Suomi', flag: '🇫🇮' },
  el: { label: 'Ελληνικά', flag: '🇬🇷' },
  tr: { label: 'Türkçe', flag: '🇹🇷' },
  ja: { label: '日本語', flag: '🇯🇵' },
  ko: { label: '한국어', flag: '🇰🇷' },
  he: { label: 'עברית', flag: '🇮🇱' },
  et: { label: 'Eesti', flag: '🇪🇪' },
  lv: { label: 'Latviešu', flag: '🇱🇻' },
  lt: { label: 'Lietuvių', flag: '🇱🇹' },
  sl: { label: 'Slovenščina', flag: '🇸🇮' },
  is: { label: 'Íslenska', flag: '🇮🇸' },
  ga: { label: 'Gaeilge', flag: '🇮🇪' },
  ru: { label: 'Русский', flag: '🇷🇺' },
  uk: { label: 'Українська', flag: '🇺🇦' },
};

export type Locale = keyof typeof LANGUAGES;

// Runtime language order — set from /api/settings/public (language_order key).
// Falls back to NEXT_PUBLIC_LANGUAGE_ORDER env var (build-time) for backward compat.
let runtimeLanguageOrder: string | null = null;

/** Called by I18nProvider after fetching public settings from backend. */
export function setRuntimeLanguageOrder(order: string | null | undefined) {
  runtimeLanguageOrder = order && order.trim() ? order.trim() : null;
}

/**
 * Returns languages ordered by runtime setting (from backend /api/settings/public),
 * falling back to NEXT_PUBLIC_LANGUAGE_ORDER env variable (build-time).
 * Format: comma-separated locale codes, e.g. "sk,cs,en,de"
 * Languages not listed come after, in their original order.
 */
export function getOrderedLanguages(): [string, { label: string; flag: string }][] {
  const entries = Object.entries(LANGUAGES);
  const orderStr = runtimeLanguageOrder || process.env.NEXT_PUBLIC_LANGUAGE_ORDER;
  if (!orderStr || !orderStr.trim()) return entries;
  const order = orderStr.split(',').map(s => s.trim()).filter(Boolean);
  const ordered: [string, { label: string; flag: string }][] = [];
  const used = new Set<string>();
  for (const code of order) {
    const lang = (LANGUAGES as Record<string, { label: string; flag: string }>)[code];
    if (lang) {
      ordered.push([code, lang]);
      used.add(code);
    }
  }
  for (const [code, lang] of entries) {
    if (!used.has(code)) ordered.push([code, lang]);
  }
  return ordered;
}

// --- Translation data: lazy-loaded per-locale JSON files ---
// `en` (fallback) and `cs` (default) are imported statically so they're always available.
// Other locales are loaded on demand via dynamic import() and cached in `localeCache`.
import enData from './locales/en.json';
import csData from './locales/cs.json';

type TranslationMap = Record<string, string>;

// Module-level cache of loaded locale data. Persists across provider re-mounts.
const localeCache: Partial<Record<Locale, TranslationMap>> = {
  en: enData as TranslationMap,
  cs: csData as TranslationMap,
};

// Track in-flight loads to avoid duplicate imports for the same locale.
const loadingPromises: Partial<Record<Locale, Promise<TranslationMap>>> = {};

async function loadLocale(loc: Locale): Promise<TranslationMap> {
  if (localeCache[loc]) return localeCache[loc]!;
  if (loadingPromises[loc]) return loadingPromises[loc]!;
  const promise = import(`./locales/${loc}.json`)
    .then((mod) => {
      const data = mod.default as TranslationMap;
      localeCache[loc] = data;
      delete loadingPromises[loc];
      return data;
    })
    .catch((err) => {
      delete loadingPromises[loc];
      console.error(`Failed to load locale '${loc}':`, err);
      // Fall back to en on failure
      return localeCache.en!;
    });
  loadingPromises[loc] = promise;
  return promise;
}

type I18nContextType = {
  locale: Locale;
  changeLocale: (newLocale: Locale) => void;
  t: (key: string) => string;
  tLocale: (targetLocale: Locale, key: string) => string;
};

const I18nContext = createContext<I18nContextType | undefined>(undefined);

let systemDefaultLocale: Locale | null = null;

function applyLocalLocale(): Locale {
  const saved = localStorage.getItem('armorik_locale');
  if (saved && saved in LANGUAGES) return saved as Locale;
  if (systemDefaultLocale) return systemDefaultLocale;
  const browserLang = navigator.language.slice(0, 2).toLowerCase();
  if (browserLang in LANGUAGES) return browserLang as Locale;
  return 'cs';
}

function getInitialLocale(): Locale {
  if (typeof window === 'undefined') return 'cs';
  try {
    const saved = localStorage.getItem('armorik_locale');
    if (saved && saved in LANGUAGES) return saved as Locale;
    const browserLang = navigator.language.slice(0, 2).toLowerCase();
    if (browserLang in LANGUAGES) return browserLang as Locale;
  } catch { /* SSR or no localStorage */ }
  return 'cs';
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocale] = useState<Locale>(getInitialLocale);
  // Bumped whenever a locale finishes loading, so resolveTranslation sees fresh data.
  // Includes the active locale and any locale requested via tLocale.
  const [loadedVersion, setLoadedVersion] = useState(0);
  const bump = () => setLoadedVersion((v) => v + 1);

  // Krok 0: načíst systémový výchozí jazyk z backendu (admin-nastavitelný)
  useEffect(() => {
    fetch(`${resolveApiBase()}/api/settings/public`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.default_locale && data.default_locale in LANGUAGES) {
          systemDefaultLocale = data.default_locale as Locale;
          if (!localStorage.getItem('armorik_locale')) {
            setLocale(systemDefaultLocale);
          }
        }
        if (data?.language_order !== undefined) {
          setRuntimeLanguageOrder(data.language_order);
          bump();
        }
      })
      .catch(() => {/* tiché selhání — zůstane lokální volba */});
  }, []);

  // Krok 1: okamžitě nastav jazyk z localStorage / prohlížeče
  useEffect(() => {
    setLocale(applyLocalLocale());
  }, []);

  // Krok 2: zkus načíst jazyk z DB profilu — má přednost
  useEffect(() => {
    const email = typeof window !== 'undefined'
      ? window.localStorage.getItem('email')
      : null;
    if (!email) return;

    fetch(`${resolveApiBase()}/api/auth/me`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((profile) => {
        if (profile?.locale && profile.locale in LANGUAGES) {
          setLocale(profile.locale as Locale);
          localStorage.setItem('armorik_locale', profile.locale);
        }
        if (profile?.permissions) {
          localStorage.setItem('permissions', JSON.stringify(profile.permissions));
          window.dispatchEvent(new Event('storage'));
        }
      })
      .catch(() => {/* tiché selhání — zůstane lokální volba */});
  }, []);

  // Krok 3: reaguj na přihlášení / odhlášení z jiného tabu
  useEffect(() => {
    const onStorage = (e: Event) => {
      // Reaguj jen na SKUTEČNÉ cross-tab StorageEvent — syntetické
      // new Event('storage') z tohoto tabu by jinak každý fetch /auth/me
      // (který zde dispatchujeme) spustil další fetch → nekonečná smyčka.
      if (!(e instanceof StorageEvent)) return;
      const email = localStorage.getItem('email');
      if (!email) {
        setLocale(applyLocalLocale());
        return;
      }
      fetch(`${resolveApiBase()}/api/auth/me`, {
        credentials: 'include',
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((profile) => {
          if (profile?.locale && profile.locale in LANGUAGES) {
            setLocale(profile.locale as Locale);
            localStorage.setItem('armorik_locale', profile.locale);
          } else {
            setLocale(applyLocalLocale());
          }
        })
        .catch(() => setLocale(applyLocalLocale()));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Krok 4: když se změní aktivní jazyk, zajisti že jeho překlady jsou načteny.
  // cs a en jsou už v cache (statický import), ostatní se donačtou async.
  useEffect(() => {
    if (localeCache[locale]) return;
    let cancelled = false;
    loadLocale(locale).then(() => {
      if (!cancelled) bump();
    });
    return () => { cancelled = true; };
  }, [locale]);

  const changeLocale = (newLocale: Locale) => {
    if (!(newLocale in LANGUAGES)) return;
    setLocale(newLocale);
    localStorage.setItem('armorik_locale', newLocale);

    // Uložit do DB (fire-and-forget, neblokuje UI)
    const email = typeof window !== 'undefined'
      ? window.localStorage.getItem('email')
      : null;
    if (email) {
      fetch(`${resolveApiBase()}/api/auth/locale`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ locale: newLocale }),
      }).catch(() => {/* tiché selhání */});
    }
  };

  const resolveTranslation = (targetLocale: Locale, key: string): string => {
    const activeTranslations = localeCache[targetLocale];
    if (activeTranslations && key in activeTranslations) {
      return activeTranslations[key];
    }
    const englishTranslations = localeCache['en']!;
    if (englishTranslations && key in englishTranslations) {
      return englishTranslations[key];
    }
    return key;
  };

  const t = (key: string): string => resolveTranslation(locale, key);

  const tLocale = (targetLocale: Locale, key: string): string => {
    // Pokud cílový jazyk ještě není načtený, vrať en fallback a donačti async.
    // Po načtení se zavolá bump() a komponenta se re-renderuje s plným překladem.
    if (!localeCache[targetLocale] && !loadingPromises[targetLocale]) {
      loadLocale(targetLocale).then(bump);
    }
    return resolveTranslation(targetLocale, key);
  };

  // loadedVersion se čte, aby React zachytil změnu při donačtení jazyka
  void loadedVersion;

  return (
    <I18nContext.Provider value={{ locale, changeLocale, t, tLocale }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
}
